import React, { useEffect, useState } from "react";
import Select from "react-select";
import productAPI from "../../services/productapi";

const ProductItemSearchSelect = ({
  value,
  onChange,
  placeholder = "Search item by ref code or product name",
  isDisabled = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  const formatOption = (item) => {
    const itemId = item.productItemId || item.id;
    const refCode = item.refCode ? ` [${item.refCode}]` : "";
    const spec = item.spec ? ` - ${item.spec}` : "";
    const unitName = item.unitName ? ` ${item.unitName}` : "";
    const label = `${item.productName || item.pro_name || "Item"}${spec}${unitName}${refCode}`;

    return {
      value: Number(itemId),
      label,
      raw: item,
    };
  };

  const mergeOptions = (existing, incoming) => {
    const map = new Map();
    [...existing, ...incoming].forEach((item) => {
      map.set(Number(item.value), item);
    });
    return Array.from(map.values());
  };

  const loadOptions = async (searchText = "") => {
    try {
      setLoading(true);

      const response = await productAPI.getAllProductItems({
        limit: 30,
        page: 0,
        status: 1,
        sortModel: [{ field: "createdAt", sort: "desc" }],
        filterModel: {
          quickFilterValues: searchText ? [searchText] : [],
        },
      });

      const rows = response?.data?.result || [];
      const mapped = rows.map(formatOption);
      setOptions((prev) => mergeOptions(prev, mapped));
    } catch (error) {
      console.error("Failed to load product items:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadSelectedValue = async (selectedId) => {
    if (!selectedId) {
      setSelectedOption(null);
      return;
    }

    const existing = options.find((opt) => Number(opt.value) === Number(selectedId));
    if (existing) {
      setSelectedOption(existing);
      return;
    }

    try {
      setLoading(true);
      const response = await productAPI.getProductItemById(selectedId);
      const item = response?.data;

      if (item) {
        const option = formatOption(item);
        setOptions((prev) => mergeOptions(prev, [option]));
        setSelectedOption(option);
      } else {
        setSelectedOption(null);
      }
    } catch (error) {
      console.error("Failed to load selected product item:", error);
      setSelectedOption(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOptions("");
  }, []);

  useEffect(() => {
    loadSelectedValue(value);
  }, [value, options.length]);

  return (
    <Select
      value={selectedOption}
      onChange={(selected) => {
        setSelectedOption(selected);
        onChange(selected ? selected.value : "");
      }}
      onInputChange={(inputValue, meta) => {
        if (meta.action === "input-change") {
          loadOptions(inputValue);
        }
      }}
      options={options}
      isLoading={loading}
      isDisabled={isDisabled}
      isClearable
      placeholder={placeholder}
      noOptionsMessage={() => "No items found"}
      loadingMessage={() => "Searching..."}
      styles={{
        control: (base) => ({
          ...base,
          minHeight: "46px",
        }),
        menu: (base) => ({
          ...base,
          zIndex: 9999,
        }),
      }}
    />
  );
};

export default ProductItemSearchSelect;